import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
    FileText, Receipt, Download, Eye, Building2, User, Mail, Phone,
    Calendar, Clock, AlertTriangle, ChevronLeft, ChevronRight, Lock,
    ExternalLink, Maximize2
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Détection iOS/iPhone
const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Détection mobile
const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * SharedPresentation - Page publique de présentation client
 * Accessible sans authentification via un token de partage temporaire
 */
export default function SharedPresentation() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentDocIndex, setCurrentDocIndex] = useState(0);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'slideshow'

    useEffect(() => {
        fetchSharedData();
    }, [token]);

    const fetchSharedData = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}`);
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Lien invalide');
            }
            const result = await res.json();
            setData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getDocumentUrl = (doc) => {
        return `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/documents/${doc.id}`;
    };

    const isEtude = (doc) => (doc?.document_type || '').toUpperCase().startsWith('ETUDE');
    const isGaz = (doc) => (doc?.document_type || '').toUpperCase().endsWith('_GAZ');
    const docLabel = (doc) => {
        const base = isEtude(doc) ? 'Étude' : 'Facture';
        return isGaz(doc) ? `${base} Gaz` : base;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const goToNext = () => {
        if (data && currentDocIndex < data.documents.length - 1) {
            setCurrentDocIndex(prev => prev + 1);
        }
    };

    const goToPrevious = () => {
        if (currentDocIndex > 0) {
            setCurrentDocIndex(prev => prev - 1);
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (viewMode === 'slideshow') {
                if (e.key === 'ArrowRight') goToNext();
                if (e.key === 'ArrowLeft') goToPrevious();
                if (e.key === 'Escape') setViewMode('grid');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [viewMode, currentDocIndex, data]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-white text-lg">Chargement de la présentation...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900 to-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full bg-white/10 backdrop-blur border-red-500/50">
                    <CardContent className="pt-6 text-center">
                        <Lock className="w-16 h-16 mx-auto mb-4 text-red-400" />
                        <h1 className="text-2xl font-bold text-white mb-2">Accès refusé</h1>
                        <p className="text-red-200">{error}</p>
                        <p className="text-slate-400 mt-4 text-sm">
                            Ce lien de partage n'est plus valide ou a expiré.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const currentDoc = data?.documents?.[currentDocIndex];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
            {/* Header */}
            <header className="bg-black/30 backdrop-blur border-b border-white/10 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-4 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0">
                            <span className="text-white font-bold text-lg sm:text-xl">V</span>
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-white font-semibold text-sm sm:text-base">Valnexo</h1>
                            <p className="text-slate-400 text-xs sm:text-sm truncate">Présentation Client</p>
                        </div>
                    </div>
                    <div className="text-right text-xs sm:text-sm shrink-0">
                        <p className="text-slate-400 truncate max-w-[130px] sm:max-w-none">Partagé par {data.created_by}</p>
                        <p className="text-slate-500 flex items-center gap-1 justify-end text-[10px] sm:text-xs">
                            <Clock className="w-3 h-3" />
                            <span className="hidden sm:inline">Expire le </span>{formatDate(data.expires_at)}
                        </p>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
                {/* Client info */}
                <Card className="bg-white/5 backdrop-blur border-white/10 mb-4 sm:mb-8">
                    <CardHeader className="p-4 sm:p-6">
                        <CardTitle className="text-white flex items-center gap-2 sm:gap-3 text-base sm:text-xl">
                            <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 shrink-0" />
                            <span className="truncate">{data.prospect_name || `Présentation étude ${data.prospect?.company || `${data.prospect?.first_name} ${data.prospect?.last_name}`}`}</span>
                        </CardTitle>
                        <CardDescription className="text-slate-400 text-xs sm:text-sm">
                            {data.documents?.length || 0} document(s) disponible(s)
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 pt-0">
                        <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm text-slate-300">
                            {data.prospect?.email && (
                                <span className="flex items-center gap-2 break-all">
                                    <Mail className="w-4 h-4 text-slate-500 shrink-0" />
                                    {data.prospect.email}
                                </span>
                            )}
                            {data.prospect?.phone && (
                                <span className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-slate-500 shrink-0" />
                                    {data.prospect.phone}
                                </span>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* View mode toggle */}
                {data.documents?.length > 0 && (
                    <div className="flex justify-center gap-2 mb-6">
                        <Button
                            variant={viewMode === 'grid' ? 'default' : 'outline'}
                            onClick={() => setViewMode('grid')}
                            className={viewMode === 'grid' ? 'bg-blue-600' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}
                        >
                            Vue Grille
                        </Button>
                        <Button
                            variant={viewMode === 'slideshow' ? 'default' : 'outline'}
                            onClick={() => { setViewMode('slideshow'); setCurrentDocIndex(0); }}
                            className={viewMode === 'slideshow' ? 'bg-blue-600' : 'bg-white/10 border-white/20 text-white hover:bg-white/20'}
                        >
                            <Eye className="w-4 h-4 mr-2" />
                            Diaporama
                        </Button>
                    </div>
                )}

                {/* Grid view */}
                {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {data.documents?.map((doc, index) => (
                            <Card 
                                key={doc.id} 
                                className="bg-white/5 backdrop-blur border-white/10 hover:bg-white/10 transition-all cursor-pointer group"
                                onClick={() => { setCurrentDocIndex(index); setViewMode('slideshow'); }}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-3">
                                        <div className={cn(
                                            "p-3 rounded-lg flex-shrink-0",
                                            isEtude(doc) 
                                                ? "bg-purple-500/20" 
                                                : "bg-amber-500/20"
                                        )}>
                                            {isEtude(doc) ? (
                                                <FileText className="w-6 h-6 text-purple-400" />
                                            ) : (
                                                <Receipt className="w-6 h-6 text-amber-400" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <Badge 
                                                variant="outline" 
                                                className={cn(
                                                    "mb-2 text-xs",
                                                    isEtude(doc)
                                                        ? "border-purple-500/50 text-purple-300"
                                                        : "border-amber-500/50 text-amber-300"
                                                )}
                                            >
                                                {docLabel(doc)}
                                            </Badge>
                                            <p className="text-white font-medium truncate">
                                                {doc.original_filename}
                                            </p>
                                            {doc.uploaded_at && (
                                                <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {/* Boutons toujours visibles sur mobile */}
                                    <div className="flex gap-2 mt-4 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                                            onClick={(e) => { e.stopPropagation(); setCurrentDocIndex(index); setViewMode('slideshow'); }}
                                        >
                                            <Eye className="w-4 h-4 mr-1" /> Voir
                                        </Button>
                                        {/* Bouton Ouvrir direct pour mobile */}
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            className="bg-blue-500/20 border-blue-400/30 text-blue-300 hover:bg-blue-500/30"
                                            asChild
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <a href={getDocumentUrl(doc)} target="_blank" rel="noopener noreferrer">
                                                <ExternalLink className="w-4 h-4" />
                                            </a>
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                                            asChild
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <a href={getDocumentUrl(doc)} download target="_blank" rel="noopener noreferrer">
                                                <Download className="w-4 h-4" />
                                            </a>
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Slideshow view */}
                {viewMode === 'slideshow' && currentDoc && (
                    <div className="fixed inset-0 bg-black z-50 flex flex-col">
                        {/* Slideshow header — compact sur mobile */}
                        <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-3 bg-slate-900 border-b border-slate-700 flex-shrink-0 gap-2">
                            <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
                                <Badge 
                                    className={cn(
                                        "text-[10px] sm:text-xs flex-shrink-0 px-1.5 py-0",
                                        isEtude(currentDoc)
                                            ? "bg-purple-500/20 text-purple-300 border-purple-500/50"
                                            : "bg-amber-500/20 text-amber-300 border-amber-500/50"
                                    )}
                                >
                                    {docLabel(currentDoc)}
                                </Badge>
                                <span className="text-white font-medium truncate text-xs sm:text-base min-w-0">
                                    {currentDoc.original_filename}
                                </span>
                                <span className="text-slate-400 text-[10px] sm:text-sm flex-shrink-0">
                                    {currentDocIndex + 1}/{data.documents.length}
                                </span>
                            </div>
                            <div className="flex items-center gap-0.5 sm:gap-2 flex-shrink-0">
                                {/* Bouton ouvrir en plein écran (important pour iOS) */}
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-blue-300 hover:text-blue-100 hover:bg-blue-500/20"
                                    asChild
                                >
                                    <a 
                                        href={getDocumentUrl(currentDoc)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                    >
                                        <ExternalLink className="w-4 h-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Ouvrir</span>
                                    </a>
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-slate-300 hover:text-white"
                                    asChild
                                >
                                    <a href={getDocumentUrl(currentDoc)} download target="_blank" rel="noopener noreferrer">
                                        <Download className="w-4 h-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Télécharger</span>
                                    </a>
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-slate-300 hover:text-white"
                                    onClick={() => setViewMode('grid')}
                                >
                                    Fermer
                                </Button>
                            </div>
                        </div>

                        {/* Document viewer - Optimisé pour mobile et desktop */}
                        <div className="flex-1 relative flex items-center justify-center overflow-hidden bg-slate-950">
                            {/* Navigation arrows - Positionnés sur les côtés */}
                            {currentDocIndex > 0 && (
                                <button
                                    onClick={goToPrevious}
                                    className="absolute left-2 sm:left-4 z-20 p-2 sm:p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all backdrop-blur-sm"
                                >
                                    <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                                </button>
                            )}
                            
                            {/* Zone principale du document */}
                            <div className="w-full h-full flex flex-col">
                                {/* Message pour iOS */}
                                {isIOS() && (
                                    <div className="bg-blue-600/90 text-white text-center py-2 px-4 text-sm flex items-center justify-center gap-2 flex-shrink-0">
                                        <ExternalLink className="w-4 h-4" />
                                        <span>Sur iPhone, cliquez sur "Ouvrir" pour voir le document complet</span>
                                    </div>
                                )}
                                
                                {/* Conteneur du document avec hauteur maximale */}
                                <div className="flex-1 w-full overflow-auto">
                                    {isIOS() ? (
                                        /* Sur iOS, utiliser object au lieu d'iframe pour une meilleure compatibilité */
                                        <object
                                            data={getDocumentUrl(currentDoc)}
                                            type="application/pdf"
                                            className="w-full h-full min-h-[calc(100dvh-140px)] sm:min-h-[calc(100vh-180px)]"
                                            style={{ backgroundColor: 'white' }}
                                        >
                                            {/* Fallback pour iOS qui ne supporte pas bien les PDF inline */}
                                            <div className="flex flex-col items-center justify-center h-full bg-slate-800 p-8 text-center">
                                                <FileText className="w-20 h-20 text-slate-400 mb-4" />
                                                <p className="text-white text-lg mb-2">Document PDF</p>
                                                <p className="text-slate-400 mb-6 max-w-md">
                                                    Pour visualiser ce document sur votre appareil, utilisez le bouton "Ouvrir" ci-dessus.
                                                </p>
                                                <Button asChild className="bg-blue-600 hover:bg-blue-700">
                                                    <a 
                                                        href={getDocumentUrl(currentDoc)} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                    >
                                                        <ExternalLink className="w-4 h-4 mr-2" />
                                                        Ouvrir le document
                                                    </a>
                                                </Button>
                                            </div>
                                        </object>
                                    ) : (
                                        /* Sur desktop et Android, iframe fonctionne bien */
                                        <iframe
                                            src={getDocumentUrl(currentDoc)}
                                            className="w-full h-full border-0 min-h-[calc(100dvh-110px)] sm:min-h-[calc(100vh-150px)]"
                                            style={{ backgroundColor: 'white' }}
                                            title={currentDoc.original_filename}
                                        />
                                    )}
                                </div>
                            </div>
                            
                            {currentDocIndex < data.documents.length - 1 && (
                                <button
                                    onClick={goToNext}
                                    className="absolute right-2 sm:right-4 z-20 p-2 sm:p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all backdrop-blur-sm"
                                >
                                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                                </button>
                            )}
                        </div>

                        {/* Progress bar */}
                        <div className="h-1 bg-slate-800 flex-shrink-0">
                            <div 
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${((currentDocIndex + 1) / data.documents.length) * 100}%` }}
                            />
                        </div>

                        {/* Thumbnails — cachés sur mobile pour libérer l'espace du document */}
                        <div className="hidden sm:flex items-center gap-2 p-2 sm:p-3 bg-slate-900 overflow-x-auto flex-shrink-0">
                            {data.documents.map((doc, index) => (
                                <button
                                    key={doc.id}
                                    onClick={() => setCurrentDocIndex(index)}
                                    className={cn(
                                        "flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0",
                                        index === currentDocIndex
                                            ? "bg-blue-600 text-white"
                                            : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                                    )}
                                >
                                    {isEtude(doc) ? (
                                        <FileText className="w-4 h-4" />
                                    ) : (
                                        <Receipt className="w-4 h-4" />
                                    )}
                                    <span className="text-sm truncate max-w-[120px] sm:max-w-[150px]">
                                        {doc.original_filename}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Keyboard hints - Hidden on mobile */}
                        <div className="hidden sm:block text-center py-2 bg-slate-900 text-slate-500 text-xs flex-shrink-0">
                            ← → Navigation • ESC Fermer
                        </div>
                    </div>
                )}

                {/* No documents */}
                {(!data.documents || data.documents.length === 0) && (
                    <Card className="bg-white/5 backdrop-blur border-white/10">
                        <CardContent className="py-12 text-center">
                            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400 opacity-50" />
                            <p className="text-white text-lg">Aucun document disponible</p>
                            <p className="text-slate-400 mt-2">
                                Les documents de ce dossier n'ont pas encore été ajoutés.
                            </p>
                        </CardContent>
                    </Card>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-black/30 border-t border-white/10 py-4 mt-8">
                <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 text-sm">
                    <p>Présentation générée par Valnexo CRM</p>
                    <p className="mt-1">Ce lien est temporaire et expire automatiquement.</p>
                </div>
            </footer>
        </div>
    );
}
